import { useCallback, createContext, useContext, useEffect, useRef, ReactNode } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useCategoryNames } from "@/hooks/use-categories";
import { useProfile } from "@/hooks/use-profile";
import { usePracticeSessions, type HistorySession } from "@/hooks/use-practice-sessions";
import { useAIConversations } from "@/hooks/use-ai-conversations";
import { SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { PracticeSidebar } from "@/components/practice/PracticeSidebar";
import { NotificationBell } from "@/components/notifications/NotificationBell";
import AvatarMenu from "@/components/layout/AvatarMenu";
import { GlobalNavSidebar } from "@/components/layout/GlobalNavSidebar";
import { useGamification } from "@/hooks/use-gamification";
import { useToast } from "@/hooks/use-toast";
import { useGamificationUIStore } from "@lyder-talk/core";
import { useTheme } from "@lyder-talk/core";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Sheet, SheetContent, useIsMobile } from "@lyder-talk/ui";
import { cn } from "@/lib/utils";
import { sidebarDividerClass } from "@/lib/card-styles";
import { AIConversationList } from "@/components/ai/ConversationSidebar";
import { useTranslation } from "react-i18next";

/**
 * My Topics routes that render without the contextual topics/conversations
 * panel. These are full-width reports about the learner — study time, recorded
 * answers — not a topic workspace, so a list of their topics beside their own
 * charts is furniture competing with the charts. Exact paths: the legacy
 * `/time` and `/speaking` aliases resolve to the same page as `/progress`.
 */
const PANEL_LESS_PATHS = new Set([
  "/my-topics/progress",
  "/my-topics/time",
  "/my-topics/speaking",
]);

interface AppShellContextType {
  isAuthenticated: boolean | undefined;
  userId: string | null;
  userEmail: string;
  CATEGORIES: string[];
  history: HistorySession[];
  pinnedSessions: string[];
  historyLoading: boolean;
  loadHistory: (userId: string) => Promise<void>;
  togglePin: (sessionId: string) => Promise<{ success: boolean; isPinned?: boolean } | undefined>;
  deleteSession: (id: string) => Promise<{ success: boolean } | undefined>;
  loadMore: (pageSize?: number) => void;
  hasMore: boolean;
  isFetchingMore: boolean;
}

const AppShellContext = createContext<AppShellContextType | null>(null);

export const useAppShellContext = () => {
  const context = useContext(AppShellContext);
  if (!context) {
    throw new Error("useAppShellContext must be used within AppShell");
  }
  return context;
};

interface AppShellProps {
  children?: ReactNode;
  /** Optional explicit sidebar type. If not provided, auto-detects from route. */
  sidebarType?: "practice" | "ai";
}

/** Global left nav + contextual history panel for My Topics routes. */
export function AppShell({ children, sidebarType }: AppShellProps) {
  return (
    <SidebarProvider>
      <AppShellInner sidebarType={sidebarType}>
        {children}
      </AppShellInner>
    </SidebarProvider>
  );
}

function AppShellInner({ children, sidebarType }: { children?: ReactNode; sidebarType?: "practice" | "ai" }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { conversationId } = useParams<{ conversationId?: string }>();

  // Auto-detect sidebar type from route if not explicitly provided
  const isAIRoute = sidebarType === "ai" || location.pathname.startsWith("/my-topics/ai");
  const showContextPanel = !PANEL_LESS_PATHS.has(location.pathname);

  const { data: isAuthenticated, userId } = useAuth();
  const { categoryNames: CATEGORIES } = useCategoryNames();
  const { profile } = useProfile();
  const { open: sidebarOpen, toggleSidebar } = useSidebar();

  const userEmail = profile?.display_name || "";

  const {
    history,
    loading: historyLoading,
    pinnedSessions,
    loadHistory,
    togglePin,
    deleteSession,
    loadMore,
    hasMore,
    isFetchingMore,
  } = usePracticeSessions(userId);

  const { level, xp, currentStreak, levelProgress, isLoading: isGamificationLoading } = useGamification();

  const { theme, resolvedTheme, setTheme } = useTheme();
  const { setStreakModalOpen } = useGamificationUIStore();

  const contextValue: AppShellContextType = {
    isAuthenticated,
    userId,
    userEmail,
    CATEGORIES,
    history,
    pinnedSessions,
    historyLoading,
    loadHistory,
    togglePin,
    deleteSession,
    loadMore,
    hasMore,
    isFetchingMore,
  };

  const isMobile = useIsMobile();
  const { openMobile, setOpenMobile } = useSidebar();

  const sidebarContent = isAIRoute ? (
    <AppShellAISidebar conversationId={conversationId} />
  ) : (
    <PracticeSidebarWrapper />
  );

  // The panel stays mounted so its width can animate; `inert` (set as an
  // attribute, React 18 has no typed prop for it) keeps the collapsed list out
  // of the tab order and the a11y tree while it is hidden.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (sidebarOpen) el.removeAttribute("inert");
    else el.setAttribute("inert", "");
  }, [sidebarOpen]);

  return (
    <AppShellContext.Provider value={contextValue}>
        <div className="flex min-h-screen w-full">
          {/* Mobile header - fixed top */}
          {isMobile && (
            <div className="fixed top-0 left-0 right-0 z-50 h-12 border-b border-stone-200 dark:border-stone-700 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex items-center justify-between px-3 md:hidden">
              {/* With no contextual panel there is nothing for the trigger to open,
                  so it is replaced by a spacer of the same size to keep the bell
                  and avatar on their existing alignment. */}
              {showContextPanel ? (
                <SidebarTrigger />
              ) : (
                <span className="h-7 w-7 shrink-0" aria-hidden />
              )}
              {isAuthenticated && (
                <div className="flex items-center gap-1">
                  <NotificationBell userId={userId} />
                  <AvatarMenu
                    gamificationProps={{
                      level,
                      xp,
                      currentStreak,
                      levelProgress,
                      isLoading: isGamificationLoading,
                    }}
                    themeProps={{ theme, resolvedTheme, setTheme }}
                    onStreakClick={() => setStreakModalOpen(true)}
                  />
                </div>
              )}
            </div>
          )}
          {/* Global nav — desktop only; carries brand, sections, notifications + avatar */}
          <GlobalNavSidebar />
          {/* Panel toggle rail — a zero-width anchor pinned right after the nav.
              Collapsed, the button centres on the nav's right edge; expanded it
              shifts by the panel's own width (w-64 = 16rem) minus half the button so
              it centres on the PANEL's right border. Either way it rides the right
              edge of whichever sidebar is on screen, on the wordmark line. */}
          {!isMobile && showContextPanel && (
            <div className="relative w-0 shrink-0">
              <button
                onClick={toggleSidebar}
                title={sidebarOpen ? t("pages.myTopics.sidebar.hidePanel", "Hide panel") : t("pages.myTopics.sidebar.showPanel", "Show panel")}
                aria-label={sidebarOpen ? t("pages.myTopics.sidebar.hidePanelAria", "Hide topics and conversations panel") : t("pages.myTopics.sidebar.showPanelAria", "Show topics and conversations panel")}
                aria-expanded={sidebarOpen}
                className={cn(
                  // The border is the seam token, so the button is the same line
                  // colour as the sidebar edge it sits on (light and dark alike).
                  "absolute left-1/2 top-[30px] z-20 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg border bg-background text-muted-foreground shadow-md transition-[transform,color,background-color] duration-200 ease-out motion-reduce:transition-none",
                  sidebarDividerClass,
                  sidebarOpen ? "translate-x-[calc(16rem-50%)]" : "-translate-x-1/2",
                )}
              >
                {sidebarOpen ? <PanelLeftClose className="h-[18px] w-[18px]" /> : <PanelLeftOpen className="h-[18px] w-[18px]" />}
              </button>
            </div>
          )}
          {/* Contextual panel (topics / conversations) - inline on desktop, Sheet on
              mobile. Always mounted: the outer element animates its width (256px → 0)
              while the inner fixed-width column fades/slides, so the list doesn't
              reflow mid-collapse. */}
          {!isMobile && showContextPanel && (
            <div
              ref={panelRef}
              aria-hidden={!sidebarOpen}
              className={cn(
                "h-screen shrink-0 overflow-hidden bg-gradient-to-br from-background via-background to-muted/20 transition-[width] duration-200 ease-out will-change-[width] motion-reduce:transition-none",
                sidebarOpen ? cn("w-64 border-r", sidebarDividerClass) : "w-0",
              )}
            >
              <div
                className={cn(
                  "h-full w-64 transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none",
                  sidebarOpen ? "translate-x-0 opacity-100" : "-translate-x-6 opacity-0",
                )}
              >
                {sidebarContent}
              </div>
            </div>
          )}
          {isMobile && showContextPanel && (
            <Sheet open={openMobile} onOpenChange={setOpenMobile}>
              <SheetContent side="left" size="full" mobileBehavior="full" className="w-64 p-0 [&>button]:hidden bg-gradient-to-br from-background via-background to-muted/20">
                {sidebarContent}
              </SheetContent>
            </Sheet>
          )}
          <div className="relative flex-1 flex flex-col h-screen min-w-0 pt-12 md:pt-0 overflow-x-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-muted/30 pointer-events-none" />
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-50 pointer-events-none" />
            <div className="absolute top-1/4 -left-24 w-72 h-72 bg-green-500/3 rounded-full blur-3xl opacity-40 pointer-events-none" />
            <div className="absolute top-1/3 -left-24 w-72 h-72 bg-secondary/5 rounded-full blur-3xl opacity-30 pointer-events-none" />
            <div className="relative flex-1 flex flex-col min-h-0">
              {children ?? <Outlet />}
            </div>
          </div>

          {/* The streak dialog is mounted once, above the routes, in
              `StreakDetailHost`. Mounting it here too opened a second copy
              on every `/my-topics/*` route, because this shell and the page
              header both subscribed to the same global store. */}
        </div>
    </AppShellContext.Provider>
  );
}

export function PracticeSidebarWrapper() {
  const {
    history,
    pinnedSessions,
    historyLoading,
    togglePin,
    deleteSession,
    loadMore,
    hasMore,
  } = useAppShellContext();

  const navigate = useNavigate();
  const { topicId } = useParams<{ topicId?: string }>();

  const handleSelectSession = useCallback((session: HistorySession) => {
    navigate(`/my-topics/edit/${session.id}`);
  }, [navigate]);

  return (
    <PracticeSidebar
      history={history}
      pinnedSessions={pinnedSessions}
      activeTopicId={topicId}
      onTogglePin={togglePin}
      onDeleteSession={deleteSession}
      onSelectSession={handleSelectSession}
      loading={historyLoading}
      onLoadMore={loadMore}
      hasMore={hasMore}
    />
  );
}

function AppShellAISidebar({ conversationId }: { conversationId?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { conversations: aiConversations, loading: aiLoading, editConversation, removeConversation } = useAIConversations();

  const handleNewAIChat = () => {
    navigate("/my-topics/ai");
  };

  const handleSelectAIConversation = (id: string) => {
    navigate(`/my-topics/ai/${id}`);
  };

  const handleEditConversation = async (id: string, newTitle: string) => {
    if (!newTitle.trim()) return;
    try {
      await editConversation(id, newTitle);
      toast({ title: t("pages.myTopics.sidebar.conversationUpdated", "Conversation updated") });
    } catch (error) {
      console.error("Error updating conversation:", error);
      toast({ title: t("common.error", "Error"), description: t("pages.myTopics.sidebar.failedToUpdateConversation", "Failed to update conversation"), variant: "destructive" });
    }
  };

  const handleDeleteConversation = async (id: string) => {
    try {
      await removeConversation(id);
      if (conversationId === id) {
        navigate("/my-topics/ai");
      }
      toast({ title: t("pages.myTopics.sidebar.conversationDeleted", "Conversation deleted") });
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast({ title: t("common.error", "Error"), description: t("pages.myTopics.sidebar.failedToDeleteConversation", "Failed to delete conversation"), variant: "destructive" });
    }
  };

  return (
    <AIConversationList
      conversations={aiConversations}
      currentConversationId={conversationId}
      onSelectConversation={handleSelectAIConversation}
      onNewChat={handleNewAIChat}
      onEditConversation={handleEditConversation}
      onDeleteConversation={handleDeleteConversation}
      loading={aiLoading}
    />
  );
}

export default AppShell;
